import React, { useCallback, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "../utils/toast";
import AdminWorkspaceNav from "../components/admin/AdminWorkspaceNav";
import { useAuth } from "../contexts/AuthContext";
import { useProcessing } from "../contexts/ProcessingContext";
import {
  createAdminBanner,
  createAdminCategoriesBanner,
  createAdminOfferCard,
  createAdminOfferBanner,
  deleteAdminBanner,
  deleteAdminOfferCard,
  deleteAdminOfferBanner,
  fetchAdminCategoriesBanner,
  fetchAdminBanners,
  fetchAdminOfferCards,
  fetchAdminOfferBanners,
  updateAdminBanner,
  updateAdminOfferCard,
  updateAdminOfferBanner,
} from "../services/adminService";
import { Banner, BannerPayload } from "../types/store";
import { optimizeImageFile } from "../utils/imageUpload";
import PageSpinner from "../components/shared/PageSpinner";
import "../styles/pages/AdminDashboardPage.css";

const emptyBanner: BannerPayload = {
  imageUrl: "",
  heading: "",
};

const AdminBannersPage: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { startProcessing, stopProcessing } = useProcessing();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [offerCards, setOfferCards] = useState<Banner[]>([]);
  const [offerBanners, setOfferBanners] = useState<Banner[]>([]);
  const [categoriesBanner, setCategoriesBanner] = useState<Banner | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingOfferCardId, setEditingOfferCardId] = useState<number | null>(null);
  const [editingOfferBannerId, setEditingOfferBannerId] = useState<number | null>(null);
  const [editingCategoriesBannerId, setEditingCategoriesBannerId] = useState<number | null>(null);
  const [formState, setFormState] = useState<BannerPayload>(emptyBanner);
  const [offerCardFormState, setOfferCardFormState] = useState<BannerPayload>(emptyBanner);
  const [offerBannerFormState, setOfferBannerFormState] = useState<BannerPayload>(emptyBanner);
  const [categoriesBannerFormState, setCategoriesBannerFormState] = useState<BannerPayload>(emptyBanner);
  const [uploading, setUploading] = useState(false);
  const [uploadingOfferCard, setUploadingOfferCard] = useState(false);
  const [uploadingOfferBanner, setUploadingOfferBanner] = useState(false);
  const [uploadingCategoriesBanner, setUploadingCategoriesBanner] = useState(false);
  const [pageReady, setPageReady] = useState(false);

  const handleAdminRequestError = useCallback((error: unknown, fallback: string) => {
    if (error instanceof AxiosError && error.response?.status === 401) {
      toast.error("Your admin session has expired. Please log in again.");
      logout();
      navigate("/admin/login", {
        replace: true,
        state: { from: location, adminOnly: true },
      });
      return;
    }

    if (error instanceof AxiosError && error.response?.status === 403) {
      toast.error("You do not have permission to manage banners.");
      return;
    }

    const responseMessage =
      error instanceof AxiosError
        ? (error.response?.data as { message?: string } | undefined)?.message
        : undefined;
    toast.error(responseMessage || fallback);
  }, [location, logout, navigate]);

  const loadBanners = useCallback(async () => {
    const processingId = startProcessing({
      title: "Loading banners",
      message: "Fetching homepage banners, offer cards, offer banners, and categories banner...",
    });
    try {
      const [bannerResponse, offerCardResponse, offerBannerResponse, categoriesBannerResponse] = await Promise.all([
        fetchAdminBanners(),
        fetchAdminOfferCards(),
        fetchAdminOfferBanners(),
        fetchAdminCategoriesBanner(),
      ]);
      setBanners(bannerResponse);
      setOfferCards(offerCardResponse);
      setOfferBanners(offerBannerResponse);
      setCategoriesBanner(categoriesBannerResponse);
      setPageReady(true);
    } catch (error) {
      handleAdminRequestError(error, "Unable to load banners right now.");
    } finally {
      stopProcessing(processingId);
    }
  }, [handleAdminRequestError, startProcessing, stopProcessing]);

  useEffect(() => {
    void loadBanners();
  }, [loadBanners]);

  return (
    <section className="shell section page-section">
      <div className="page-header admin-page-header">
        <div>
          <span className="eyebrow">Admin workspace</span>
          <h1>Banner management</h1>
        </div>
      </div>
      <AdminWorkspaceNav />

      {!pageReady ? (
        <section className="store-card admin-panel admin-panel--full">
          <PageSpinner label="Loading banners..." />
        </section>
      ) : null}

      {pageReady ? (
        <>
      <section className="store-card admin-panel admin-panel--full">
        <div className="admin-panel__heading">
          <div>
            <span className="eyebrow">Banner form</span>
            <h2>
              {editingId ? "Edit banner" : "Upload banner"}
            </h2>
          </div>
        </div>
        <form
          className="form-grid"
          onSubmit={async (event) => {
            event.preventDefault();
            const processingId = startProcessing({
              title: editingId ? "Updating banner" : "Creating banner",
              message: "Saving the banner image and refreshing the homepage...",
            });
            try {
              if (editingId) {
                await updateAdminBanner(editingId, formState);
                toast.success("Banner image updated");
              } else {
                await createAdminBanner(formState);
                toast.success("Banner image created");
              }
              setEditingId(null);
              setFormState(emptyBanner);
              await loadBanners();
            } catch (error) {
              handleAdminRequestError(error, "Unable to save banner right now.");
            } finally {
              stopProcessing(processingId);
            }
          }}
        >
          <label className="form-grid__wide">
            Banner heading
            <input
              type="text"
              value={formState.heading || ""}
              onChange={(event) =>
                setFormState((current) => ({ ...current, heading: event.target.value }))
              }
              placeholder="Summer Deals"
              required
            />
          </label>
          <label className="form-grid__wide">
            Banner image
            <input
              type="file"
              accept="image/*"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (!file) {
                  return;
                }
                setUploading(true);
                try {
                  const imageUrl = await optimizeImageFile(file);
                  setFormState((current) => ({ ...current, imageUrl }));
                } catch {
                  toast.error("Unable to prepare selected banner image.");
                } finally {
                  setUploading(false);
                  event.target.value = "";
                }
              }}
              required={!formState.imageUrl}
            />
          </label>
          {formState.imageUrl ? (
            <div className="form-grid__wide admin-image-preview-grid">
              <div className="admin-image-preview-card">
                <img src={formState.imageUrl} alt="Banner preview" />
                <button
                  type="button"
                  className="link-button"
                  onClick={() => setFormState((current) => ({ ...current, imageUrl: "" }))}
                >
                  Remove image
                </button>
              </div>
            </div>
          ) : null}
          <div className="admin-form-actions">
            <button className="button" type="submit" disabled={uploading}>
              {uploading ? (
                <span className="button-loading">
                  <span className="button-loading__spinner" aria-hidden="true" />
                  Preparing...
                </span>
              ) : editingId ? (
                "Update image"
              ) : (
                "Create image"
              )}
            </button>
          </div>
        </form>
      </section>

      <section className="store-card admin-panel admin-panel--full">
        <div className="admin-panel__heading">
          <div>
            <span className="eyebrow">Banner list</span>
            <h2>
              Homepage banners <span className="admin-banner-dimensions">(1600 × 592 px)</span>
            </h2>
          </div>
        </div>
        <div className="admin-table">
          <table>
            <thead>
              <tr>
                <th>Heading</th>
                <th>Slug</th>
                <th>Image</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {banners.length ? banners.map((banner) => (
                <tr key={banner.id}>
                  <td>{banner.heading || "Untitled banner"}</td>
                  <td>{banner.slug || "-"}</td>
                  <td>
                    {banner.imageUrl ? (
                      <img
                        src={banner.imageUrl}
                        alt={`Banner ${banner.id}`}
                        style={{ width: "100%", maxWidth: 420, borderRadius: 18, display: "block" }}
                      />
                    ) : (
                      "No image"
                    )}
                  </td>
                  <td>
                    <div className="admin-table-actions">
                      <button
                        className="link-button"
                        type="button"
                        onClick={() => {
                          setEditingId(banner.id);
                          setFormState({
                            imageUrl: banner.imageUrl || "",
                            heading: banner.heading || "",
                          });
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="link-button admin-danger-button"
                        type="button"
                        onClick={async () => {
                          try {
                            await deleteAdminBanner(banner.id);
                            setBanners((current) => current.filter((item) => item.id !== banner.id));
                            if (editingId === banner.id) {
                              setEditingId(null);
                              setFormState(emptyBanner);
                            }
                            toast.success("Banner image deleted");
                            await loadBanners();
                          } catch (error) {
                            handleAdminRequestError(error, "Unable to delete banner right now.");
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={4}>
                    <div className="admin-empty-note">No homepage banners have been added yet.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="store-card admin-panel admin-panel--full">
        <div className="admin-panel__heading">
          <div>
            <span className="eyebrow">Offer cards</span>
            <h2>
              {editingOfferCardId ? "Edit offer card" : "Upload offer card"}{" "}
              <span className="admin-banner-dimensions">(200 px height)</span>
            </h2>
          </div>
        </div>
        <form
          className="form-grid"
          onSubmit={async (event) => {
            event.preventDefault();
            const processingId = startProcessing({
              title: editingOfferCardId ? "Updating offer card" : "Creating offer card",
              message: "Saving the offer card image and refreshing the homepage...",
            });
            try {
              if (editingOfferCardId) {
                await updateAdminOfferCard(editingOfferCardId, offerCardFormState);
                toast.success("Offer card updated");
              } else {
                await createAdminOfferCard(offerCardFormState);
                toast.success("Offer card created");
              }
              setEditingOfferCardId(null);
              setOfferCardFormState(emptyBanner);
              await loadBanners();
            } catch (error) {
              handleAdminRequestError(error, "Unable to save offer card right now.");
            } finally {
              stopProcessing(processingId);
            }
          }}
        >
          <label>
            Offer card label
            <input
              type="text"
              value={offerCardFormState.heading || ""}
              onChange={(event) =>
                setOfferCardFormState((current) => ({ ...current, heading: event.target.value }))
              }
              placeholder="Big Savings"
              required
            />
          </label>
          <label>
            Offer card image
            <input
              type="file"
              accept="image/*"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (!file) {
                  return;
                }
                setUploadingOfferCard(true);
                try {
                  const imageUrl = await optimizeImageFile(file);
                  setOfferCardFormState((current) => ({ ...current, imageUrl }));
                } catch {
                  toast.error("Unable to prepare selected offer card image.");
                } finally {
                  setUploadingOfferCard(false);
                  event.target.value = "";
                }
              }}
              required={!offerCardFormState.imageUrl}
            />
          </label>
          {offerCardFormState.imageUrl ? (
            <div className="form-grid__wide admin-image-preview-grid">
              <div className="admin-image-preview-card">
                <img src={offerCardFormState.imageUrl} alt="Offer card preview" />
                <button
                  type="button"
                  className="link-button"
                  onClick={() => setOfferCardFormState((current) => ({ ...current, imageUrl: "" }))}
                >
                  Remove image
                </button>
              </div>
            </div>
          ) : null}
          <div className="admin-form-actions">
            <button className="button" type="submit" disabled={uploadingOfferCard}>
              {uploadingOfferCard ? (
                <span className="button-loading">
                  <span className="button-loading__spinner" aria-hidden="true" />
                  Preparing...
                </span>
              ) : editingOfferCardId ? (
                "Update offer card"
              ) : (
                "Create offer card"
              )}
            </button>
            {editingOfferCardId ? (
              <button
                className="link-button"
                type="button"
                onClick={() => {
                  setEditingOfferCardId(null);
                  setOfferCardFormState(emptyBanner);
                }}
              >
                Cancel edit
              </button>
            ) : null}
          </div>
        </form>
      </section>

      <section className="store-card admin-panel admin-panel--full">
        <div className="admin-panel__heading">
          <div>
            <span className="eyebrow">Offer cards list</span>
            <h2>Homepage offer cards</h2>
          </div>
        </div>
        <div className="admin-table">
          <table>
            <thead>
              <tr>
                <th>Label</th>
                <th>Image</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {offerCards.length ? offerCards.map((banner) => (
                <tr key={banner.id}>
                  <td>{banner.heading || "Untitled banner"}</td>
                  <td>
                    {banner.imageUrl ? (
                      <img
                        src={banner.imageUrl}
                        alt={`Offer card ${banner.id}`}
                        style={{ width: "100%", maxWidth: 260, height: 120, objectFit: "cover", borderRadius: 8, display: "block" }}
                      />
                    ) : (
                      "No image"
                    )}
                  </td>
                  <td>
                    <div className="admin-table-actions">
                      <button
                        className="link-button"
                        type="button"
                        onClick={() => {
                          setEditingOfferCardId(banner.id);
                          setOfferCardFormState({
                            imageUrl: banner.imageUrl || "",
                            heading: banner.heading || "",
                          });
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="link-button admin-danger-button"
                        type="button"
                        onClick={async () => {
                          try {
                            await deleteAdminOfferCard(banner.id);
                            setOfferCards((current) => current.filter((item) => item.id !== banner.id));
                            if (editingOfferCardId === banner.id) {
                              setEditingOfferCardId(null);
                              setOfferCardFormState(emptyBanner);
                            }
                            toast.success("Offer card deleted");
                            await loadBanners();
                          } catch (error) {
                            handleAdminRequestError(error, "Unable to delete offer card right now.");
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={3}>
                    <div className="admin-empty-note">No homepage offer cards have been added yet.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="store-card admin-panel admin-panel--full">
        <div className="admin-panel__heading">
          <div>
            <span className="eyebrow">Offer banners</span>
            <h2>
              {editingOfferBannerId ? "Edit offer banner" : "Upload offer banner"}{" "}
              <span className="admin-banner-dimensions">(3 in a row)</span>
            </h2>
          </div>
        </div>
        <form
          className="form-grid"
          onSubmit={async (event) => {
            event.preventDefault();
            const processingId = startProcessing({
              title: editingOfferBannerId ? "Updating offer banner" : "Creating offer banner",
              message: "Saving the offer banner image and refreshing the homepage...",
            });
            try {
              if (editingOfferBannerId) {
                await updateAdminOfferBanner(editingOfferBannerId, offerBannerFormState);
                toast.success("Offer banner updated");
              } else {
                await createAdminOfferBanner(offerBannerFormState);
                toast.success("Offer banner created");
              }
              setEditingOfferBannerId(null);
              setOfferBannerFormState(emptyBanner);
              await loadBanners();
            } catch (error) {
              handleAdminRequestError(error, "Unable to save offer banner right now.");
            } finally {
              stopProcessing(processingId);
            }
          }}
        >
          <label>
            Offer banner label
            <input
              type="text"
              value={offerBannerFormState.heading || ""} 
              onChange={(event) =>
                setOfferBannerFormState((current) => ({ ...current, heading: event.target.value }))
              }
              placeholder="Big Savings"
              required
            />
          </label>
          <label>
            Offer banner image
            <input
              type="file"
              accept="image/*"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (!file) {
                  return;
                }
                setUploadingOfferBanner(true);
                try {
                  const imageUrl = await optimizeImageFile(file);
                  setOfferBannerFormState((current) => ({ ...current, imageUrl }));
                } catch {
                  toast.error("Unable to prepare selected offer banner image.");
                } finally {
                  setUploadingOfferBanner(false);
                  event.target.value = "";
                }
              }}
              required={!offerBannerFormState.imageUrl}
            />
          </label>
          {offerBannerFormState.imageUrl ? (
            <div className="form-grid__wide admin-image-preview-grid">
              <div className="admin-image-preview-card">
                <img src={offerBannerFormState.imageUrl} alt="Offer banner preview" />
                <button
                  type="button"
                  className="link-button"
                  onClick={() => setOfferBannerFormState((current) => ({ ...current, imageUrl: "" }))}
                >
                  Remove image
                </button>
              </div>
            </div>
          ) : null}
          <div className="admin-form-actions">
            <button className="button" type="submit" disabled={uploadingOfferBanner}>
              {uploadingOfferBanner ? (
                <span className="button-loading">
                  <span className="button-loading__spinner" aria-hidden="true" />
                  Preparing...
                </span>
              ) : editingOfferBannerId ? (
                "Update offer banner"
              ) : (
                "Create offer banner"
              )}
            </button>
            {editingOfferBannerId ? (
              <button
                className="link-button"
                type="button"
                onClick={() => {
                  setEditingOfferBannerId(null);
                  setOfferBannerFormState(emptyBanner);
                }}
              >
                Cancel edit
              </button>
            ) : null}
          </div>
        </form>
      </section>

      <section className="store-card admin-panel admin-panel--full">
        <div className="admin-panel__heading">
          <div>
            <span className="eyebrow">Offer banners list</span>
            <h2>Homepage offer banners</h2>
          </div>
        </div>
        <div className="admin-table">
          <table>
            <thead>
              <tr>
                <th>Label</th>
                <th>Image</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {offerBanners.length ? offerBanners.map((banner) => (
                <tr key={banner.id}>
                  <td>{banner.heading || "Untitled banner"}</td>
                  <td>
                    {banner.imageUrl ? (
                      <img
                        src={banner.imageUrl}
                        alt={`Offer banner ${banner.id}`}
                        style={{ width: "100%", maxWidth: 260, height: 120, objectFit: "cover", borderRadius: 8, display: "block" }}
                      />
                    ) : (
                      "No image"
                    )}
                  </td>
                  <td>
                    <div className="admin-table-actions">
                      <button
                        className="link-button"
                        type="button"
                        onClick={() => {
                          setEditingOfferBannerId(banner.id);
                          setOfferBannerFormState({
                            imageUrl: banner.imageUrl || "",
                            heading: banner.heading || "",
                          });
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="link-button admin-danger-button"
                        type="button"
                        onClick={async () => {
                          try {
                            await deleteAdminOfferBanner(banner.id);
                            setOfferBanners((current) => current.filter((item) => item.id !== banner.id));
                            if (editingOfferBannerId === banner.id) {
                              setEditingOfferBannerId(null);
                              setOfferBannerFormState(emptyBanner);
                            }
                            toast.success("Offer banner deleted");
                            await loadBanners();
                          } catch (error) {
                            handleAdminRequestError(error, "Unable to delete offer banner right now.");
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={3}>
                    <div className="admin-empty-note">No homepage offer banners have been added yet.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="store-card admin-panel admin-panel--full">
        <div className="admin-panel__heading">
          <div>
            <span className="eyebrow">Categories banner</span>
            <h2>
              {editingCategoriesBannerId ? "Edit categories page banner" : "Upload categories page banner"}{" "}
              <span className="admin-banner-dimensions">(1600 × 592 px)</span>
            </h2>
          </div>
        </div>
        <form
          className="form-grid"
          onSubmit={async (event) => {
            event.preventDefault();
            const processingId = startProcessing({
              title: editingCategoriesBannerId ? "Updating categories banner" : "Creating categories banner",
              message: "Saving the banner image and refreshing the categories page...",
            });
            try {
              if (editingCategoriesBannerId) {
                await updateAdminBanner(editingCategoriesBannerId, categoriesBannerFormState);
                toast.success("Categories page banner updated");
              } else {
                await createAdminCategoriesBanner(categoriesBannerFormState);
                toast.success("Categories page banner created");
              }
              setEditingCategoriesBannerId(null);
              setCategoriesBannerFormState(emptyBanner);
              await loadBanners();
            } catch (error) {
              handleAdminRequestError(error, "Unable to save categories page banner right now.");
            } finally {
              stopProcessing(processingId);
            }
          }}
        >
          <label className="form-grid__wide">
            Banner heading
            <input
              type="text"
              value={categoriesBannerFormState.heading || ""}
              onChange={(event) =>
                setCategoriesBannerFormState((current) => ({ ...current, heading: event.target.value }))
              }
              placeholder="Shop by Category"
            />
          </label>
          <label className="form-grid__wide">
            Banner image
            <input
              type="file"
              accept="image/*"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (!file) {
                  return;
                }
                setUploadingCategoriesBanner(true);
                try {
                  const imageUrl = await optimizeImageFile(file);
                  setCategoriesBannerFormState((current) => ({ ...current, imageUrl }));
                } catch {
                  toast.error("Unable to prepare selected categories banner image.");
                } finally {
                  setUploadingCategoriesBanner(false);
                  event.target.value = "";
                }
              }}
              required={!categoriesBannerFormState.imageUrl}
            />
          </label>
          {categoriesBannerFormState.imageUrl ? (
            <div className="form-grid__wide admin-image-preview-grid">
              <div className="admin-image-preview-card">
                <img src={categoriesBannerFormState.imageUrl} alt="Categories banner preview" />
                <button
                  type="button"
                  className="link-button"
                  onClick={() => setCategoriesBannerFormState((current) => ({ ...current, imageUrl: "" }))}
                >
                  Remove image
                </button>
              </div>
            </div>
          ) : null}
          <div className="admin-form-actions">
            <button className="button" type="submit" disabled={uploadingCategoriesBanner}>
              {uploadingCategoriesBanner ? (
                <span className="button-loading">
                  <span className="button-loading__spinner" aria-hidden="true" />
                  Preparing...
                </span>
              ) : editingCategoriesBannerId ? (
                "Update banner"
              ) : (
                "Create banner"
              )}
            </button>
            {editingCategoriesBannerId ? (
              <button
                className="link-button"
                type="button"
                onClick={() => {
                  setEditingCategoriesBannerId(null);
                  setCategoriesBannerFormState(emptyBanner);
                }}
              >
                Cancel edit
              </button>
            ) : null}
          </div>
        </form>
      </section>

      <section className="store-card admin-panel admin-panel--full">
        <div className="admin-panel__heading">
          <div>
            <span className="eyebrow">Categories banner preview</span>
            <h2>Current categories page image</h2>
          </div>
        </div>
        <div className="admin-table">
          <table>
            <thead>
              <tr>
                <th>Heading</th>
                <th>Image</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categoriesBanner ? (
                <tr>
                  <td>{categoriesBanner.heading || "Untitled banner"}</td>
                  <td>
                    {categoriesBanner.imageUrl ? (
                      <img
                        src={categoriesBanner.imageUrl}
                        alt={`Categories banner ${categoriesBanner.id}`}
                        style={{ width: "100%", maxWidth: 320, height: 120, objectFit: "cover", borderRadius: 8, display: "block" }}
                      />
                    ) : (
                      "No image"
                    )}
                  </td>
                  <td>
                    <div className="admin-table-actions">
                      <button
                        className="link-button"
                        type="button"
                        onClick={() => {
                          setEditingCategoriesBannerId(categoriesBanner.id);
                          setCategoriesBannerFormState({
                            imageUrl: categoriesBanner.imageUrl || "",
                            heading: categoriesBanner.heading || "",
                          });
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="link-button admin-danger-button"
                        type="button"
                        onClick={async () => {
                          try {
                            await deleteAdminBanner(categoriesBanner.id);
                            setCategoriesBanner(null);
                            setEditingCategoriesBannerId(null);
                            setCategoriesBannerFormState(emptyBanner);
                            toast.success("Categories page banner deleted");
                            await loadBanners();
                          } catch (error) {
                            handleAdminRequestError(error, "Unable to delete categories banner right now.");
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={3}>
                    <div className="admin-empty-note">No categories page banner has been added yet.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
        </>
      ) : null}
    </section>
  );
};

export default AdminBannersPage;
